import React from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { IconType } from 'react-icons';
import { matchesSearch } from '../../Utilities/searchText';

export type SearchOption = {
  value: any;
  label: string;
  /** Other names the row answers to, so a search in one language finds a
   *  label shown in another. Never displayed. */
  keywords?: (string | null | undefined)[];
  icon?: IconType;
  color?: string;
  disabled?: boolean;
};

/** The menu's highlight colours. A screen with its own accent passes its own;
 *  the rest take the dashboard's. */
export type SearchMenuTone = {
  hover: string;
  hoverText?: string;
  selected: string;
  selectedHover: string;
  selectedText?: string;
};

// The typed text only narrows the list. Nothing outside a listed option can
// be picked (no freeSolo), so a half-typed word is dropped on blur and the
// field shows its saved pick again.
const filterBy = (options: SearchOption[], { inputValue }: { inputValue: string }) =>
  options.filter((option) =>
    matchesSearch(inputValue, [option.label, ...(option.keywords || [])]),
  );

const useMenuTone = (tone?: SearchMenuTone): SearchMenuTone => {
  const T = useTheme().palette.dash;
  return (
    tone || {
      hover: alpha(T.accent, 0.06),
      selected: alpha(T.accent, 0.1),
      selectedHover: alpha(T.accent, 0.14),
    }
  );
};

const PINNED = 'search-pinned';

// Autocomplete paints its own focused / selected rows; these outrank that so
// the menu reads like the plain dropdowns it replaced. Arrow keys and the
// mouse both move `.Mui-focused`, so it stands in for :hover.
//
// Action rows (e.g. "Add a city") stay at the foot of the list while it
// scrolls. They take the paper's own colour so the rows sliding under them
// do not show through, and layer the highlight over it rather than replace it.
const menuSx = (tone: SearchMenuTone, divider: string) => ({
  '& .MuiAutocomplete-listbox': { backgroundColor: 'inherit' },
  '& .MuiAutocomplete-listbox .MuiAutocomplete-option': {
    gap: '6px',
    minHeight: 40,
    '&.Mui-focused': { backgroundColor: tone.hover, color: tone.hoverText },
    '&[aria-selected="true"]': {
      backgroundColor: tone.selected,
      color: tone.selectedText,
    },
    '&[aria-selected="true"].Mui-focused': {
      backgroundColor: tone.selectedHover,
    },
    [`&.${PINNED}`]: {
      position: 'sticky',
      bottom: 0,
      zIndex: 1,
      backgroundColor: 'inherit',
      borderTop: `1px solid ${divider}`,
      '&.Mui-focused': {
        backgroundColor: 'inherit',
        backgroundImage: `linear-gradient(${tone.hover}, ${tone.hover})`,
      },
    },
  },
});

const OptionContent = ({ option }: { option: SearchOption }) => {
  const { t } = useTranslation(['main']);
  return (
    <>
      {option.icon && (
        <Box
          component='span'
          sx={{ display: 'inline-flex', '& > svg': { fontSize: '1.2rem' } }}
        >
          <option.icon />
        </Box>
      )}
      {/* Separators off: a geo name carrying ":" would otherwise be read as
          a namespace and rendered as its own tail. */}
      {t(option.label, { nsSeparator: false, keySeparator: false })}
    </>
  );
};

type SingleProps = {
  id?: string;
  name?: string;
  value: any;
  options: SearchOption[];
  pinned?: SearchOption[];
  onChange: (value: any) => void;
  onOpen?: () => void;
  /** The list is still on its way — the menu says so instead of "No results". */
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  small?: boolean;
  paperSx?: SxProps<Theme>;
  tone?: SearchMenuTone;
  fieldSx?: SxProps<Theme>;
};

/** A single pick out of a long list, searchable by typing. Emits the option's
 *  value, or '' when cleared — the same values the plain dropdown sent. */
export const SearchableSelect = ({
  id,
  name,
  value,
  options,
  pinned = [],
  onChange,
  onOpen,
  loading,
  disabled,
  placeholder,
  error,
  helperText,
  small,
  paperSx,
  tone,
  fieldSx,
}: SingleProps) => {
  const { t } = useTranslation(['main']);
  const theme = useTheme();
  const menuTone = useMenuTone(tone);
  const pinnedValues = new Set(pinned.map((option) => option.value));
  const selected =
    options.find((option) => option.value === value && value !== '') || null;

  return (
    <Autocomplete<SearchOption, false, false, false>
      id={id}
      fullWidth
      size={small ? 'small' : 'medium'}
      options={[...options, ...pinned]}
      value={selected}
      disabled={disabled}
      onOpen={onOpen}
      onChange={(_, option) => onChange(option ? option.value : '')}
      filterOptions={(list, state) => [
        ...filterBy(
          list.filter((option) => !pinnedValues.has(option.value)),
          state,
        ),
        ...pinned,
      ]}
      isOptionEqualToValue={(option, current) => option.value === current.value}
      // Keyed by value: MUI keys rows by label, and two places can share a name.
      getOptionKey={(option) => String(option.value)}
      getOptionLabel={(option) =>
        t(option.label, { nsSeparator: false, keySeparator: false })
      }
      getOptionDisabled={(option) => Boolean(option.disabled)}
      noOptionsText={t('No results')}
      loading={loading}
      loadingText={t('Loading...')}
      slotProps={{
        paper: {
          sx: [
            menuSx(menuTone, theme.palette.divider),
            ...(Array.isArray(paperSx) ? paperSx : [paperSx ?? false]),
          ],
        },
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key: React.Key };
        return (
          <li
            key={key}
            {...rest}
            className={
              pinnedValues.has(option.value)
                ? `${rest.className} ${PINNED}`
                : rest.className
            }
            style={{ color: option.color }}
          >
            <OptionContent option={option} />
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          dir={document.dir}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          sx={fieldSx}
        />
      )}
    />
  );
};

/** The rounded shape the filter panels' dropdowns share. */
export const FILTER_INPUT_SX = { borderRadius: '20px', minHeight: 52 };

type MultiProps = {
  label?: string;
  name?: string;
  values: string[];
  options: SearchOption[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  /** Rides on the outlined input, so each filter panel keeps its own shape. */
  inputSx?: React.CSSProperties;
  tone?: SearchMenuTone;
};

/** Several picks out of a long list, searchable by typing. Picks show as
 *  removable tags; the menu stays open and ticks what is already chosen. */
export const SearchableMultiSelect = ({
  label,
  name,
  values,
  options,
  onChange,
  disabled,
  error,
  placeholder,
  inputSx,
  tone,
}: MultiProps) => {
  const { t } = useTranslation(['main']);
  const theme = useTheme();
  const menuTone = useMenuTone(tone);

  // A saved pick the list has not reached yet still shows as a tag, under its
  // value, rather than vanishing from the field.
  const selected = values.map(
    (value) =>
      options.find((option) => option.value === value) || {
        value,
        label: value,
      },
  );

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography variant='body2' sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}
      <Autocomplete<SearchOption, true, false, false>
        multiple
        fullWidth
        disableCloseOnSelect
        options={options}
        value={selected}
        disabled={disabled}
        onChange={(_, picked) => onChange(picked.map((option) => option.value))}
        filterOptions={filterBy}
        isOptionEqualToValue={(option, current) => option.value === current.value}
        getOptionKey={(option) => String(option.value)}
        getOptionLabel={(option) => option.label}
        getOptionDisabled={(option) => Boolean(option.disabled)}
        noOptionsText={t('No results')}
        limitTags={2}
        getLimitTagsText={(more) => `+${more}`}
        slotProps={{ paper: { sx: menuSx(menuTone, theme.palette.divider) } }}
        renderOption={(props, option, { selected: ticked }) => {
          const { key, ...rest } = props as typeof props & { key: React.Key };
          return (
            <li key={key} {...rest}>
              <Checkbox
                size='small'
                checked={ticked}
                sx={{ p: 0, marginInlineEnd: '4px' }}
              />
              {option.label}
            </li>
          );
        }}
        renderTags={(tags, getTagProps) =>
          tags.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} size='small' label={option.label} {...tagProps} />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            dir={document.dir}
            error={error}
            placeholder={values.length ? undefined : placeholder}
            inputProps={{ ...params.inputProps, 'aria-label': label }}
            sx={{ '& .MuiOutlinedInput-root': inputSx ?? {} }}
          />
        )}
      />
    </Box>
  );
};
